import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { getApiErrorMessage } from "@/lib/api";
import PublicBreadcrumb from "@/components/public/PublicBreadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Shield, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Errore di accesso"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen auth-grain flex">
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1483959651481-dc75b89291f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGRhdGElMjBzZWN1cml0eXxlbnwwfHx8fDE3ODM1OTg4NTJ8MA&ixlib=rb-4.1.0&q=85"
          alt="security"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/70 via-slate-900/50 to-blue-900/40" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-blue-600 flex items-center justify-center">
              <Shield className="h-6 w-6" strokeWidth={2} />
            </div>
            <div>
              <div className="font-display font-black text-2xl">Audit Pro</div>
              <div className="text-xs uppercase tracking-[0.25em] text-slate-300">GDPR · NIS2 · ISO · DORA · AI Act</div>
            </div>
          </div>
          <div className="max-w-md">
            <h2 className="font-display font-black text-4xl leading-tight mb-4">Audit rapidi, relazioni professionali.</h2>
            <p className="text-slate-300 leading-relaxed">Checklist preconfigurate sui provvedimenti del Garante Privacy e sui requisiti NIS2. Genera relazioni conclusive con AI in pochi minuti.</p>
            <div className="mt-8 font-mono-plex text-xs text-slate-400 border-l-2 border-blue-500 pl-4">
              GDPR · Reg. UE 2016/679<br/>
              NIS2 · Dir. UE 2022/2555 · D.Lgs. 138/2024<br/>
              ISO/IEC 27001:2022 · ISMS<br/>
              DORA · Reg. UE 2022/2554<br/>
              AI Act · Reg. UE 2024/1689
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12 relative z-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-md bg-slate-900 flex items-center justify-center">
              <Shield className="h-6 w-6 text-white" strokeWidth={2} />
            </div>
            <div className="font-display font-black text-2xl">Audit Pro</div>
          </div>
          <PublicBreadcrumb currentLabel="Login" />
          <h1 className="font-display font-black text-3xl mb-2">Accedi</h1>
          <p className="text-slate-500 text-sm mb-8">Entra nella piattaforma o torna al sito pubblico per vedere piani e prodotto.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-600">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 h-11 bg-white"
                placeholder="nome@studio.it"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-600">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="h-11 bg-white pr-11"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  title={showPassword ? "Nascondi password" : "Mostra password"}
                  className="absolute right-0 top-0 h-11 w-11 flex items-center justify-center text-slate-400 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" data-testid="login-submit-btn" disabled={loading} className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium">
              {loading ? "Accesso…" : "Accedi"} <ArrowRight className="h-4 w-4" />
            </Button>
          </form>

          <p className="mt-8 text-sm text-slate-500 text-center">
            Gli account vengono creati dall'amministratore. Contattalo per ricevere l'accesso.
          </p>
          <p className="mt-3 text-xs text-center text-slate-400">
            Vuoi vedere il prodotto prima di entrare? <Link to="/" className="text-slate-600 hover:text-slate-950">Torna alla homepage</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
